import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core';
import styled from 'styled-components';

import {
  baseUrl,
  buildMcpConsentPath,
  savePendingGenericMcpConsent
} from 'framework';

const Shell = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.spacing(2)}px;
`;

const Card = styled(Box)`
  max-width: 460px;
  width: 100%;
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 16px;
  padding: ${p => p.theme.spacing(4)}px;
  background: ${p => p.theme.palette.background.paper};
  box-shadow: 0 24px 60px rgba(0, 48, 96, 0.12);
  text-align: center;
`;

const Actions = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(1.5)}px;
  margin-top: ${p => p.theme.spacing(3)}px;
`;

// The generic OAuth endpoints live at the host root (/mcp/...), not under /api.
const apiRoot = baseUrl.replace(/\/api\/?$/, '');

type Status = 'loading' | 'consent';

/**
 * Generic (marketplace) MCP consent entry screen. An MCP client hits the
 * org-agnostic /mcp/oauth/authorize without a workspace and the server
 * redirects here. The user signs in:
 *  - SSO (Google/Microsoft) → account-level platform login → workspace picker;
 *  - or a workspace id → that workspace's own login (incl. password).
 * Either way they land on the chosen workspace's consent screen, which
 * completes the OAuth authorize — the same proven flow the per-org connector
 * uses (issue #252).
 */
export const McpConsentGeneric = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [clientName, setClientName] = useState('');
  const [showWorkspaceSignin, setShowWorkspaceSignin] = useState(false);
  const [workspaceId, setWorkspaceId] = useState('');

  const search = window.location.search;
  const clientId = new URLSearchParams(search).get('client_id') || '';

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      if (clientId) {
        try {
          const response = await fetch(
            `${apiRoot}/mcp/oauth/client?client_id=${encodeURIComponent(clientId)}`
          );
          if (active && response.ok) {
            const data = (await response.json()) as { client_name?: string };
            setClientName(data.client_name || '');
          }
        } catch {
          // Ignore lookup failure and use generic consent copy.
        }
      }
      if (active) {
        setStatus('consent');
      }
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, [clientId]);

  const startSignin = (provider: 'google' | 'microsoft') => {
    // Stash the OAuth request so it survives platform login; the callback shows
    // the workspace picker and resumes on the picked workspace's consent screen.
    savePendingGenericMcpConsent(search);
    window.location.href = `${baseUrl}/auth/${provider}/start`;
  };

  const continueWithWorkspace = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const slug = workspaceId.trim().toLowerCase();
    if (!slug) {
      return;
    }
    // Hand off to that workspace's own consent screen, which supports its
    // configured sign-in (including password) and completes the authorize.
    window.location.href = buildMcpConsentPath(slug, search);
  };

  if (status === 'loading') {
    return (
      <Shell>
        <Card>
          <CircularProgress />
          <Typography style={{ marginTop: 16 }}>{t('loading')}</Typography>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <Typography variant="h6" gutterBottom>
          {t('mcpConsentTitle')}
        </Typography>
        <Typography color="textSecondary">
          {t('mcpConsentGenericBody', {
            client: clientName || t('mcpConsentTitle')
          })}
        </Typography>
        <Actions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => startSignin('google')}
          >
            {t('mcpConsentGenericGoogle')}
          </Button>
          <Button variant="outlined" onClick={() => startSignin('microsoft')}>
            {t('mcpConsentGenericMicrosoft')}
          </Button>
          {!showWorkspaceSignin ? (
            <Button onClick={() => setShowWorkspaceSignin(true)}>
              {t('signInWithPassword')}
            </Button>
          ) : null}
        </Actions>
        {showWorkspaceSignin ? (
          <Box mt={2} textAlign="left">
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t('signinOrgIdHint')}
            </Typography>
            <form onSubmit={continueWithWorkspace}>
              <TextField
                label={t('orgId')}
                value={workspaceId}
                fullWidth
                variant="outlined"
                size="small"
                onChange={event =>
                  setWorkspaceId(event.target.value.toLowerCase())
                }
              />
              <Box mt={1.5}>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!workspaceId.trim()}
                  onClick={continueWithWorkspace}
                >
                  {t('mcpConsentGenericContinue')}
                </Button>
              </Box>
            </form>
          </Box>
        ) : null}
      </Card>
    </Shell>
  );
};
